// Relatório principal: movimentação do período por produto — saldo inicial, entradas, saídas,
// ajustes e saldo final, em unidade e R$ (saldo_final = inicial + entradas − saídas ± ajustes).
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { FileSpreadsheet, FileText } from "lucide-react";
import { getEstoqueCategorias, getEstoqueProdutos, getEstoqueRelatorioMovimentacao } from "../../api";
import EstoqueLayout from "./EstoqueLayout";
import { MESES, ProdutoSelect, formatData } from "./comum";
import { AvisoErro, Campo, btnStyle, formatBRL, formatQtd, inputStyle, palette, useIsMobile } from "./ui";

const COLUNAS = [
  { id: "inicial", label: "Saldo inicial" },
  { id: "entradas", label: "Entradas" },
  { id: "saidas", label: "Saídas" },
  { id: "ajustes", label: "Ajustes" },
  { id: "final", label: "Saldo final" },
];

export default function RelatorioMovimentacaoView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const hoje = new Date();
  const [filtro, setFiltro] = useState({ ano: hoje.getFullYear(), mes: hoje.getMonth() + 1, categoria_id: "", produto_id: null });
  const [visao, setVisao] = useState("ambos"); // qtd | valor | ambos
  const [soComMovimento, setSoComMovimento] = useState(true);
  const [cad, setCad] = useState({ categorias: [], produtos: [] });
  const [rel, setRel] = useState(null);
  const [erro, setErro] = useState("");

  useEffect(() => {
    Promise.all([getEstoqueCategorias(), getEstoqueProdutos()]).then(([categorias, produtos]) => setCad({ categorias, produtos })).catch((e) => setErro(e.message));
  }, []);
  const carregar = useCallback(() => {
    getEstoqueRelatorioMovimentacao(filtro).then(setRel).catch((e) => setErro(e.message));
  }, [filtro]);
  useEffect(() => { carregar(); }, [carregar]);

  const itens = useMemo(() => (rel?.itens || []).filter((i) => !soComMovimento ||
    [i.qtd_inicial, i.qtd_entradas, i.qtd_saidas, i.qtd_ajustes, i.valor_final].some((v) => Number(v) !== 0)), [rel, soComMovimento]);
  const grupos = useMemo(() => {
    const m = new Map();
    for (const i of itens) {
      if (!m.has(i.categoria_nome)) m.set(i.categoria_nome, []);
      m.get(i.categoria_nome).push(i);
    }
    return Array.from(m.entries());
  }, [itens]);
  const soma = (lista, campo) => lista.reduce((s, i) => s + Number(i[campo]), 0);
  const anos = Array.from({ length: 4 }, (_, k) => hoje.getFullYear() - 2 + k);

  function linhasExport() {
    return itens.map((i) => ({
      Categoria: i.categoria_nome, Produto: i.produto_nome, Unidade: i.unidade_sigla,
      "Qtd inicial": Number(i.qtd_inicial), "Qtd entradas": Number(i.qtd_entradas), "Qtd saídas": Number(i.qtd_saidas),
      "Qtd ajustes": Number(i.qtd_ajustes), "Qtd final": Number(i.qtd_final),
      "R$ inicial": Number(i.valor_inicial), "R$ entradas": Number(i.valor_entradas), "R$ saídas": Number(i.valor_saidas),
      "R$ ajustes": Number(i.valor_ajustes), "R$ final": Number(i.valor_final),
      "Custo médio final": i.custo_medio_final === null ? "" : Number(i.custo_medio_final),
    }));
  }
  const nomeArquivo = `movimentacao_estoque_${filtro.ano}_${String(filtro.mes).padStart(2, "0")}`;
  function exportarExcel() {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(linhasExport()), "Movimentação");
    XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
  }
  function exportarCsv() {
    const ws = XLSX.utils.json_to_sheet(linhasExport());
    const csv = XLSX.utils.sheet_to_csv(ws, { FS: ";" });
    const blob = new Blob(["﻿" + csv.replace(/(\d)\.(\d)/g, "$1,$2")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${nomeArquivo}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }

  const mostraQtd = visao !== "valor";
  const mostraValor = visao !== "qtd";
  const th = (align = "right", extra = {}) => ({ padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", background: C.card, position: "sticky", top: 0, ...extra });
  const td = (align = "right", extra = {}) => ({ padding: "7px 10px", fontSize: 13, textAlign: align, borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap", ...extra });
  const corColuna = (id, v) => (id === "saidas" && Number(v) ? C.danger : id === "entradas" && Number(v) ? C.success : id === "ajustes" && Number(v) ? C.warning : C.text);

  return (
    <EstoqueLayout titulo="Movimentação do Período" isLight={isLight} setIsLight={setIsLight}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-end", marginBottom: 12 }}>
        <div style={{ flex: "0 1 150px" }}>
          <Campo label="Mês" C={C}>
            <select value={filtro.mes} onChange={(e) => setFiltro({ ...filtro, mes: Number(e.target.value) })} style={inputStyle(C)}>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </Campo>
        </div>
        <div style={{ flex: "0 1 110px" }}>
          <Campo label="Ano" C={C}>
            <select value={filtro.ano} onChange={(e) => setFiltro({ ...filtro, ano: Number(e.target.value) })} style={inputStyle(C)}>
              {anos.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Campo>
        </div>
        <div style={{ flex: "0 1 170px" }}>
          <Campo label="Categoria" C={C}>
            <select value={filtro.categoria_id} onChange={(e) => setFiltro({ ...filtro, categoria_id: e.target.value })} style={inputStyle(C)}>
              <option value="">Todas</option>
              {cad.categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </Campo>
        </div>
        <div style={{ flex: "1 1 220px" }}><Campo label="Produto" C={C}><ProdutoSelect C={C} produtos={cad.produtos} value={filtro.produto_id} onChange={(id) => setFiltro({ ...filtro, produto_id: id })} /></Campo></div>
        <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
          {[["qtd", "Unidades"], ["valor", "R$"], ["ambos", "Ambos"]].map(([id, label]) => (
            <button key={id} onClick={() => setVisao(id)} style={{ padding: "10px 12px", minHeight: 42, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 700, background: visao === id ? C.brand : C.card, color: visao === id ? "#fff" : C.muted }}>{label}</button>
          ))}
        </div>
        <button onClick={exportarExcel} disabled={!itens.length} style={{ ...btnStyle(C.success), opacity: itens.length ? 1 : 0.5 }}><FileSpreadsheet size={15} /> Excel</button>
        <button onClick={exportarCsv} disabled={!itens.length} style={{ ...btnStyle(C.success, true), opacity: itens.length ? 1 : 0.5 }}><FileText size={15} /> CSV</button>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, marginBottom: 10, cursor: "pointer" }}>
        <input type="checkbox" checked={soComMovimento} onChange={(e) => setSoComMovimento(e.target.checked)} /> Só produtos com saldo ou movimento
      </label>

      {rel && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
            {COLUNAS.map((c) => (
              <div key={c.id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{c.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2, color: corColuna(c.id, soma(itens, `valor_${c.id}`)) }}>{formatBRL(soma(itens, `valor_${c.id}`))}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{formatData(rel.inicio)} a {formatData(rel.fim)} · {itens.length} produto(s)</div>

          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {itens.map((i) => (
                <button key={i.produto_id} onClick={() => navigate(`/estoque/kardex?produto=${i.produto_id}&inicio=${rel.inicio}&fim=${rel.fim}`)}
                  style={{ textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, fontFamily: "inherit", color: C.text, cursor: "pointer" }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{i.produto_nome}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{i.categoria_nome} · {i.unidade_sigla}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 4, fontSize: 11 }}>
                    {COLUNAS.map((c) => (
                      <div key={c.id}>
                        <div style={{ color: C.muted }}>{c.label.replace("Saldo ", "")}</div>
                        {mostraQtd && <div style={{ fontWeight: 700, color: corColuna(c.id, i[`qtd_${c.id}`]) }}>{formatQtd(i[`qtd_${c.id}`])}</div>}
                        {mostraValor && <div style={{ color: mostraQtd ? C.muted : corColuna(c.id, i[`valor_${c.id}`]), fontWeight: mostraQtd ? 400 : 700 }}>{formatBRL(i[`valor_${c.id}`])}</div>}
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "62vh" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th("left")} rowSpan={visao === "ambos" ? 2 : 1}>Produto</th>
                    {COLUNAS.map((c) => <th key={c.id} style={th(visao === "ambos" ? "center" : "right")} colSpan={visao === "ambos" ? 2 : 1}>{c.label}</th>)}
                  </tr>
                  {visao === "ambos" && (
                    <tr>
                      {COLUNAS.map((c) => [
                        <th key={`${c.id}q`} style={th("right", { top: 33 })}>Qtd.</th>,
                        <th key={`${c.id}v`} style={th("right", { top: 33 })}>R$</th>,
                      ])}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {grupos.map(([categoria, lista]) => [
                    <tr key={`g-${categoria}`} style={{ background: C.bgActive }}>
                      <td style={td("left", { fontWeight: 800 })}>{categoria}</td>
                      {COLUNAS.map((c) => [
                        mostraQtd && <td key={`${c.id}q`} style={td()} />,
                        mostraValor && <td key={`${c.id}v`} style={td("right", { fontWeight: 800 })}>{formatBRL(soma(lista, `valor_${c.id}`))}</td>,
                      ])}
                    </tr>,
                    ...lista.map((i) => (
                      <tr key={i.produto_id}>
                        <td style={td("left")}>
                          <button onClick={() => navigate(`/estoque/kardex?produto=${i.produto_id}&inicio=${rel.inicio}&fim=${rel.fim}`)} title="Ver extrato"
                            style={{ background: "none", border: "none", padding: 0, color: C.text, cursor: "pointer", fontFamily: "inherit", fontSize: 13, textAlign: "left" }}>
                            {i.produto_nome} <span style={{ color: C.muted, fontSize: 11 }}>({i.unidade_sigla})</span>
                          </button>
                        </td>
                        {COLUNAS.map((c) => [
                          mostraQtd && <td key={`${c.id}q`} style={td("right", { color: corColuna(c.id, i[`qtd_${c.id}`]), fontWeight: c.id === "final" ? 700 : 400 })}>{formatQtd(i[`qtd_${c.id}`])}</td>,
                          mostraValor && <td key={`${c.id}v`} style={td("right", { fontWeight: c.id === "final" ? 700 : 400, color: visao === "ambos" ? C.muted : corColuna(c.id, i[`valor_${c.id}`]) })}>{formatBRL(i[`valor_${c.id}`])}</td>,
                        ])}
                      </tr>
                    )),
                  ])}
                  {itens.length === 0 && <tr><td colSpan={11} style={td("center", { color: C.muted, padding: 24 })}>Nenhum produto no período.</td></tr>}
                </tbody>
                {itens.length > 0 && mostraValor && (
                  <tfoot>
                    <tr>
                      <td style={td("left", { fontWeight: 800 })}>Total</td>
                      {COLUNAS.map((c) => [
                        mostraQtd && <td key={`${c.id}q`} style={td()} />,
                        <td key={`${c.id}v`} style={td("right", { fontWeight: 800 })}>{formatBRL(soma(itens, `valor_${c.id}`))}</td>,
                      ])}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </>
      )}
    </EstoqueLayout>
  );
}
